import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./OnboardingPage.css";

function OnboardingPage() {
  const navigate = useNavigate();
  // Start at step 1 (IGN Setup), assuming the user is authenticated and needs setup.
  const [step, setStep] = useState(1);

  // Form data states
  const [ignsInput, setIgnsInput] = useState("");
  const [clanRole, setClanRole] = useState("None");
  const [newClanName, setNewClanName] = useState("");
  const [selectedClanId, setSelectedClanId] = useState("");

  // UI/Auth states
  const [status, setStatus] = useState({
    message: "Verifying user data...",
    color: "gray",
  });
  const [existingClans, setExistingClans] = useState([]);
  // Tracks if the final setup API call is in progress
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch clans for clan selection
  const fetchClans = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/clans`,
        {
          credentials: "include",
        }
      );
      if (response.ok) {
        const data = await response.json();
        setExistingClans(data.clans || []);
        if (data.clans.length > 0 && selectedClanId === "") {
          setSelectedClanId(data.clans[0].id);
        }
      } else {
        console.error("Failed to fetch existing clans.");
      }
    } catch (error) {
      console.error("Network error fetching clans:", error);
    }
  };

  // Auth check and setup step logic
  const checkAuthAndSetup = async () => {
    try {
      // Fetch user data (This acts as the final authentication check and data fetch)
      const userResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/user/me`,
        {
          credentials: "include",
        }
      );

      if (userResponse.ok) {
        const userData = await userResponse.json();

        // If IGNs exist, setup is complete. Redirect to dashboard.
        if (userData.igns && userData.igns.length > 0) {
          setStatus({
            message: "Setup complete. Redirecting to dashboard...",
            color: "green",
          });
          // Small delay before redirecting
          setTimeout(() => navigate("/dashboard", { replace: true }), 100);
          return;
        }

        // If no IGNs, proceed to step 1 (IGN setup form)
        setStatus({
          message: "Step 1/2: Please enter your OSRS In-Game Names (IGNs)...",
          color: "blue",
        });
        setStep(1);

        // Start fetching clans in the background
        fetchClans();
      } else {
        // If user/me fails (e.g., session expired just now), redirect back to login
        console.error("User/me fetch failed, redirecting to login.");
        navigate("/", { replace: true });
      }
    } catch (error) {
      // Catch network errors
      console.error("Error during setup auth check:", error);
      navigate("/", { replace: true });
    }
  };

  const handleIgnSubmit = (e) => {
    e.preventDefault();
    const ignsArray = ignsInput
      .split(",")
      .map((ign) => ign.trim())
      .filter((ign) => ign.length > 0 && ign.length <= 12);

    if (ignsArray.length === 0) {
      setStatus({
        message: "Please enter at least one valid IGN.",
        color: "red",
      });
      return;
    }

    setStatus({ message: "Step 2/2: Clan membership details.", color: "blue" });
    setStep(2);
  };

  const handleFinalSetupSubmit = async (e) => {
    e.preventDefault();

    // Prevent double submission via the button handler
    if (isSubmitting) return;

    let clanPayload = {};
    if (clanRole === "Owner") {
      if (!newClanName.trim()) {
        setStatus({
          message: "Clan Owner must enter a Clan Name.",
          color: "red",
        });
        return;
      }
      clanPayload = { role: "Owner", clan_name: newClanName.trim() };
    } else if (clanRole === "Member" || clanRole === "Guest") {
      if (!selectedClanId || existingClans.length === 0) {
        setStatus({
          message: `Clan ${clanRole} must select an existing Clan.`,
          color: "red",
        });
        return;
      }
      // Use the current clanRole (Member or Guest) and the selected ID
      clanPayload = { role: clanRole, clan_id: selectedClanId };
    } else {
      clanPayload = { role: "None" };
    }

    setStatus({
      message: "Finalizing setup (IGNs and Clan)...",
      color: "gray",
    });

    // START SUBMISSION LOCK
    setIsSubmitting(true);

    const finalIgnsArray = ignsInput
      .split(",")
      .map((ign) => ign.trim())
      .filter((ign) => ign.length > 0 && ign.length <= 12);

    const payload = {
      igns: finalIgnsArray,
      clan_setup: clanPayload,
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/setup`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          credentials: "include",
        }
      );

      if (response.ok) {
        const data = await response.json();
        setStatus({
          message: `Setup complete! Registered ${data.ign_count} IGN(s). Redirecting to dashboard...`,
          color: "green",
        });
        // SUCCESS: Redirect happens here. isSubmitting stays true until redirection.
        setTimeout(() => navigate("/dashboard"), 1500);
      } else {
        const errorText = await response.text();
        let errorData = { error: `Server error: ${response.statusText}` };
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {}
        setStatus({
          message: `Error during setup: ${
            errorData.error || errorData.details || "Unknown error"
          }`,
          color: "red",
        });
        // FAILURE: Unlock isSubmitting so the user can try again.
        setIsSubmitting(false);
      }
    } catch (error) {
      setStatus({
        message: "Network error: Could not connect to the backend.",
        color: "red",
      });
      console.error("Final setup submission error:", error);
      // CATCH FAILURE: Unlock isSubmitting so the user can try again.
      setIsSubmitting(false);
    }
    // Removed 'finally' block to prevent flicker on success.
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("auth") === "success") {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    checkAuthAndSetup();
  }, [navigate]);

  const renderIgnSetup = () => (
    <form onSubmit={handleIgnSubmit} className="ign-setup-form">
      <p className="welcome-text">
        To use the game, first link your Old School RuneScape In-Game Names
        (IGNs).
        <br />
        Enter them below, separated by commas. This is permanent.
      </p>
      <p className="case-warning">
        <strong>Must be EXACTLY case-sensitive.</strong>
        <br />
        (e.g., <code>My IgN</code> is different from <code>my ign</code>)
      </p>
      <input
        type="text"
        value={ignsInput}
        onChange={(e) => setIgnsInput(e.target.value)}
        placeholder="e.g., RunescapeName1, Second IGN (case-sensitive)"
        className="ign-input"
        required
      />
      <button type="submit" className="submit-button">
        Continue to Clan Setup &rarr;
      </button>
    </form>
  );

  const renderClanSetup = () => {
    // Determine if the form input is invalid (excluding network state)
    let isFinalSubmitDisabled = false;

    if (clanRole === "Owner" && !newClanName.trim()) {
      isFinalSubmitDisabled = true;
    } else if (clanRole === "Member" || clanRole === "Guest") {
      // Disabled if no clans exist OR if clans exist but none is selected
      if (existingClans.length === 0 || !selectedClanId) {
        isFinalSubmitDisabled = true;
      }
    }

    // The button is disabled if the input is invalid OR if submission is in progress
    const isDisabled = isFinalSubmitDisabled || isSubmitting;

    return (
      <form onSubmit={handleFinalSetupSubmit} className="ign-setup-form">
        <p className="welcome-text">
          <strong>Step 2/2:</strong> Clan Membership. Do you represent a clan?
        </p>

        <div className="radio-group">
          <label>
            <input
              type="radio"
              value="Owner"
              checked={clanRole === "Owner"}
              onChange={() => setClanRole("Owner")}
              disabled={isSubmitting}
            />
            <span className="radio-label-text">
              I am a <strong>Clan Owner</strong> (I will register a new Clan)
            </span>
          </label>

          <label>
            <input
              type="radio"
              value="Member"
              checked={clanRole === "Member"}
              onChange={() => setClanRole("Member")}
              disabled={isSubmitting}
            />
            <span className="radio-label-text">
              I am a <strong>Clan Member</strong> (I will join an existing Clan)
            </span>
          </label>

          <label>
            <input
              type="radio"
              value="Guest"
              checked={clanRole === "Guest"}
              onChange={() => setClanRole("Guest")}
              disabled={isSubmitting}
            />
            <span className="radio-label-text">
              I am a <strong>Clan Guest</strong> (I will join an existing Clan
              as a guest)
            </span>
          </label>

          <label>
            <input
              type="radio"
              value="None"
              checked={clanRole === "None"}
              onChange={() => setClanRole("None")}
              disabled={isSubmitting}
            />
            <span className="radio-label-text">
              I do not want to set up a Clan yet (Skip)
            </span>
          </label>
        </div>

        {clanRole === "Owner" && (
          <input
            type="text"
            value={newClanName}
            onChange={(e) => setNewClanName(e.target.value)}
            placeholder="Enter the name of your new Clan"
            className="ign-input"
            required
            disabled={isSubmitting}
          />
        )}

        {/* Clan selection is needed for both Member and Guest */}
        {(clanRole === "Member" || clanRole === "Guest") && (
          <>
            {existingClans.length > 0 ? (
              <select
                className="ign-input"
                value={selectedClanId}
                onChange={(e) => setSelectedClanId(e.target.value)}
                required
                disabled={isSubmitting}
              >
                {existingClans.map((clan) => (
                  <option key={clan.id} value={clan.id}>
                    {clan.name}
                  </option>
                ))}
              </select>
            ) : (
              <p style={{ color: "orange", marginTop: "10px" }}>
                No existing clans found. Please select "Owner" or "Skip" for
                now.
              </p>
            )}
          </>
        )}

        <button
          type="submit"
          className="submit-button"
          // Disable if invalid OR submitting
          disabled={isDisabled}
        >
          {isSubmitting ? "Processing..." : "Finish Setup & Go to Dashboard"}
        </button>
        <button
          type="button"
          onClick={() => {
            setStep(1);
            setStatus({
              message: "Step 1/2: Please enter your OSRS IGNs.",
              color: "blue",
            });
          }}
          className="back-button"
          disabled={isSubmitting}
        >
          &larr; Back to IGNs
        </button>
      </form>
    );
  };

  if (step === 0) {
    return (
      <div className="ign-setup-page">
        <h2>Loading Setup...</h2>
        <p style={{ color: status.color }}>{status.message}</p>
      </div>
    );
  }

  return (
    <div className="ign-setup-page">
      <h2>Initial Setup</h2>

      {step === 1 && renderIgnSetup()}
      {step === 2 && renderClanSetup()}

      <p className="status-message" style={{ color: status.color }}>
        {status.message}
      </p>
    </div>
  );
}

export default OnboardingPage;
